import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const IMG = {
  warehouse: "/assets/images/new/14.jpg",
  whoWeAre: "/assets/images/new/2.jpg",
  visionMission: "/assets/images/new/3.jpg",
  freshProduce: "/assets/images/new/4.jpg",
  supplyChain: "/assets/images/new/5.jpg",
  globalNetwork: "/assets/images/new/6.jpg",
  customers: "/assets/images/new/7.jpg",
  strength: "/assets/images/new/8.jpg",
  contact: "/assets/images/new/9.jpg",
  pineappleDole: "/assets/images/new/10.jpg",
  bananaDole: "/assets/images/new/11.jpg",
  pineappleOla: "/assets/images/new/12.jpg",
  pineappleOla2: "/assets/images/new/13.jpg",
  bananaTerioka: "/assets/images/new/15.png",
  whyPartner: "/assets/images/new/13.jpg",
  new19: "/assets/images/new/21.jpg",
  new20: "/assets/images/new/20.jpg",
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= bp : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return isMobile;
}


function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

function useInView(ref, threshold = 0.12) {
  const [vis, setVis] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold },
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return vis;
}

function useStickyProgress(ref) {
  const [prog, setProg] = useState(0);
  useEffect(() => {
    const fn = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const total = ref.current.offsetHeight - window.innerHeight;
      setProg(clamp(-rect.top / total, 0, 1));
    };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return prog;
}

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;overflow-x:hidden;background:#fff}
  @keyframes fadeUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeOnly{from{opacity:0}to{opacity:1}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes scrollDot{0%{transform:translateY(-100%)}100%{transform:translateY(250%)}}
  @keyframes pulseGreen{0%,100%{box-shadow:0 0 0 0 rgba(221,60,48,0.4)}50%{box-shadow:0 0 0 14px rgba(221,60,48,0)}}
  @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
  @keyframes countUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeSlideUp{0%{opacity:0;transform:translateY(40px)}100%{opacity:1;transform:translateY(0)}}
  @keyframes popIn{from{opacity:0;transform:scale(0.7)}to{opacity:1;transform:scale(1)}}
  @keyframes bounceIn{0%{transform:scale(0.3);opacity:0}50%{transform:scale(1.05)}70%{transform:scale(0.9)}100%{transform:scale(1);opacity:1}}
  @keyframes orbPulse{0%,100%{transform:scale(1);opacity:0.6}50%{transform:scale(1.15);opacity:1}}
  @keyframes glowPulse{0%,100%{box-shadow:0 0 0 0 rgba(221,60,48,0.5)}50%{box-shadow:0 0 30px 8px rgba(221,60,48,0.15)}}
  @keyframes ripple{0%{transform:scale(0);opacity:1}100%{transform:scale(4);opacity:0}}
  @keyframes cardSlideIn{from{opacity:0;transform:translateY(60px) scale(0.94)}to{opacity:1;transform:translateY(0) scale(1)}}
  .ani-fadeup{animation:fadeUp 0.85s cubic-bezier(.22,1,.36,1) both}
  .ani-fade{animation:fadeOnly 0.85s ease both}
  .serif{font-family:'Georgia',Georgia,serif}
  .shine-text{
    background:linear-gradient(90deg,#dd3c30 0%,#fff 40%,#dd3c30 60%,#fff 100%);
    background-size:300% 100%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;animation:shimmer 4s linear infinite
  }
  .progress-pill{width:8px;height:8px;border-radius:4px;background:rgba(255,255,255,0.3);transition:all 0.4s ease}
  .progress-pill.active{width:28px;background:#dd3c30}
  .sl{font-size:11px;letter-spacing:3px;color:#dd3c30;font-weight:700;text-transform:uppercase;margin-bottom:14px}
  .sl-dark{font-size:11px;letter-spacing:3px;color:#276530;font-weight:700;text-transform:uppercase;margin-bottom:14px}
  .btn-green{display:inline-block;background:#276530;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-green:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(39,101,48,0.45)}
  .btn-ghost{display:inline-block;background:rgba(255,255,255,0.12);color:#fff;padding:13px 40px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;border:1.5px solid rgba(255,255,255,0.3);transition:background 0.2s;cursor:pointer}
  .btn-ghost:hover{background:rgba(255,255,255,0.22)}
  .nav-secondary{position:sticky;top:56px;z-index:900;background:rgba(13,13,13,0.97);backdrop-filter:blur(16px);border-bottom:1px solid rgba(255,255,255,0.06);display:flex;align-items:center;justify-content:center;padding:0 24px;height:48px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
  .nav-secondary::-webkit-scrollbar{display:none}
  .nav-secondary a{color:rgba(255,255,255,0.55);font-size:14px;font-weight:400;text-decoration:none;padding:0 18px;height:100%;display:flex;align-items:center;border-bottom:2px solid transparent;transition:all 0.2s;white-space:nowrap;flex-shrink:0}
  .nav-secondary a:hover{color:#fff}
  .nav-secondary a.active{color:#dd3c30;border-color:#dd3c30}
  .cta-input{width:100%;padding:16px 22px;border-radius:14px;border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.07);color:#fff;font-size:15px;outline:none;transition:border-color 0.3s,background 0.3s}
  .cta-input:focus{border-color:rgba(221,60,48,0.6);background:rgba(255,255,255,0.1)}
  .cam-tab-btn{transition:all 0.3s cubic-bezier(.22,1,.36,1)}
  .cam-tab-btn.active{background:linear-gradient(135deg,#163d1f,#276530)!important;color:#fff!important;box-shadow:0 8px 24px rgba(39,101,48,0.4);transform:translateY(-2px)}
  .btn-red{display:inline-block;background:#dd3c30;color:#fff;padding:13px 50px;border-radius:10px;text-decoration:none;font-weight:500;font-size:15px;transition:transform 0.2s,box-shadow 0.2s;border:none;cursor:pointer;box-shadow:0 6px 20px rgba(39,101,48,0.35)}
  .btn-red:hover{transform:scale(1.04);box-shadow:0 10px 28px rgba(101, 39, 39, 0.45)}
  img{max-width:100%}
  .nav-burger{display:none}
  @media (max-width:900px){
    .nav-links-desktop, .nav-call-desktop{display:none!important}
    .nav-burger{display:flex!important}
    .nav-inner{gap:16px!important}
  }
  @media (max-width:900px){
    .nav-secondary{justify-content:flex-start;padding:0 14px}
  }
  @media (max-width:640px){
    .nav-secondary a{padding:0 12px;font-size:13px}
  }
`;

export function Navbar({ scrollY }) {
  const scrolled = scrollY > 80;
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = ["Home", "About Us", "Products", "Contact Us"];

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background:
          scrolled || menuOpen ? "rgba(255,255,255,0.97)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(14px)" : "none",
        boxShadow: scrolled || menuOpen ? "0 1px 0 rgba(0,0,0,0.07)" : "none",
        transition: "all 0.4s ease",
        height: 56,
      }}
    >
      <div
        className="nav-inner"
        style={{
          maxWidth: 1312,
          margin: "0 auto",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          height: "100%",
          gap: 70,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <img src="/assets/images/new/logo.png" width="50" alt="Fruits Garden logo" />
          <div style={{ marginLeft: 20 }}>
            <h3
              style={{
                letterSpacing: -1,
                marginBottom: -5,
                color: scrolled || menuOpen ? "#000" : "#fff",
                transition: "color 0.4s ease",
                whiteSpace: "nowrap",
              }}
            >
              Fruits Garden
            </h3>
            <p
              style={{
                fontSize: 14,
                color: scrolled || menuOpen ? "#000" : "#fff",
                transition: "color 0.4s ease",
                whiteSpace: "nowrap",
              }}
            >
              Quality Comes First
            </p>
          </div>
        </div>

        <nav className="nav-links-desktop" style={{ display: "flex", gap: 3, flex: 1 }}>
          {navLinks.map((n) => (
            <Link
              key={n}
              to={`/${n.toLowerCase().replace(" ", "-")}`}
              style={{
                padding: "5px 20px",
                borderRadius: 50,
                textDecoration: "none",
                backgroundColor: "#00523c",
                fontSize: 15,
                color: "#fff",
                fontWeight: 400,
                transition: "background 0.18s",
                whiteSpace: "nowrap",
              }}
            >
              {n}
            </Link>
          ))}
        </nav>
        <a
          href="tel:+971 56 537 8650"
          className="btn-red nav-call-desktop"
          style={{
            color: "#fff",
            border: "none",
            borderRadius: 50,
            padding: "8px 30px",
            fontSize: 14,
            fontWeight: 400,
            cursor: "pointer",
            textDecoration: "none",
            whiteSpace: "nowrap",
            marginLeft: "auto",
          }}
        >
          Call Now
        </a>

        <button
          className="nav-burger"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((v) => !v)}
          style={{
            display: "none",
            marginLeft: "auto",
            width: 40,
            height: 40,
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 26,
            color: scrolled || menuOpen ? "#000" : "#fff",
            flexShrink: 0,
          }}
        >
          <i className={menuOpen ? "bi bi-x-lg" : "bi bi-list"} />
        </button>
      </div>

      <div
        className="nav-mobile-panel"
        style={{
          maxHeight: menuOpen ? 480 : 0,
          overflow: "hidden",
          transition: "max-height 0.35s ease",
          background: "#fff",
          borderTop: menuOpen ? "1px solid rgba(0,0,0,0.08)" : "none",
        }}
      >
        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "10px 24px 20px",
            gap: 6,
          }}
        >
          {navLinks.map((n) => (
            <Link
              key={n}
              to={`/${n.toLowerCase().replace(" ", "-")}`}
              onClick={() => setMenuOpen(false)}
              style={{
                padding: "12px 16px",
                borderRadius: 10,
                textDecoration: "none",
                backgroundColor: "#f4f4f2",
                fontSize: 15,
                color: "#111",
                fontWeight: 500,
              }}
            >
              {n}
            </Link>
          ))}
          <a
            href="tel:+971 56 537 8650"
            className="btn-red"
            style={{
              marginTop: 8,
              textAlign: "center",
              color: "#fff",
              borderRadius: 10,
              padding: "12px 16px",
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            Call Now
          </a>
        </nav>
      </div>
    </header>
  );
}

function HeroSection({ scrollY }) {
  const pY = scrollY * 0.35;
  const tOp = clamp(1 - scrollY / 480, 0, 1);
  const tY = scrollY * 0.18;

  return (
    <section
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
        background: "#080808",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `translateY(${pY}px)`,
        }}
      >
        <img
          src="/assets/images/company/1.jpeg"
          alt=""
          style={{
            width: "100%",
            height: "112%",
            objectFit: "cover",
            filter: "brightness(0.7) saturate(1.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom,rgba(0,0,0,0.15) 0%,rgba(0,0,0,0.55) 55%,rgba(0,0,0,0.92) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at 70% 50%,rgba(45,1.3,30,0.2) 0%,transparent 60%)",
          }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 8% 0 8%",
          transform: `translateY(${tY}px)`,
          opacity: tOp,
        }}
      >
        <div
          className="ani-fadeup"
          style={{
            animationDelay: "0.05s",
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 36,
              height: 2,
              background: "#dd3c30",
              borderRadius: 1,
            }}
          />
          <span
            style={{
              fontSize: 12,
              letterSpacing: 3,
              color: "#dd3c30",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            Quality Comes First
          </span>
        </div>
        <h1
          className="ani-fadeup serif"
          style={{
            animationDelay: "0.15s",
            fontSize: "clamp(48px,6.5vw,45px)",
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: -3,
            marginBottom: 20,
            color: "#fff",
            maxWidth: 700,
          }}
        >
          Freshness You Can Trust.
          <br />
          <span className="shine-text">Quality You Deserve.</span>
        </h1>
        <p
          className="ani-fadeup"
          style={{
            animationDelay: "0.3s",
            fontSize: "clamp(16px,1.6vw,19px)",
            maxWidth: 520,
            lineHeight: 1.3,
            color: "rgba(255,255,255,0.7)",
            marginBottom: 52,
          }}
        >
          Fruits Garden Trading LLC — Dubai's premier fresh fruits & vegetables
          supplier to HORECA. Premium quality, reliable delivery, competitive
          pricing since 2002.
        </p>

        <div
          className="ani-fadeup"
          style={{
            animationDelay: "0.52s",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <a
            href="#highlights"
            className="btn-red"
            style={{ fontSize: 15, padding: "14px min(45px,4.5vw)" }}
          >
            Our Products
          </a>
          <a
            href="#story"
            className="btn-ghost"
            style={{ fontSize: 15, padding: "14px min(45px,4.5vw)" }}
          >
            Who We Are
          </a>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          color: "rgba(255,255,255,0.4)",
          opacity: tOp,
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
          }}
        >
          Scroll down
        </span>
        <div
          style={{
            width: 1.5,
            height: 48,
            background: "rgba(255,255,255,0.15)",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "40%",
              background: "#dd3c30",
              animation: "scrollDot 1.6s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function SecondaryNav({ scrollY }) {
  const anchors = [
    ["#highlights", "Overview"],
    ["#products", "Products"],
    ["#freshness", "Freshness"],
    ["#supply", "Supply Chain"],
    ["#global", "Global Network"],
    ["#customers", "Customers"],
    ["#strength", "Strength"],
    ["#story", "About Us"],
  ];
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  useEffect(() => {
    const sections = anchors.map(([id]) => document.querySelector(id));
    const idx = sections.reduceRight((acc, el, i) => {
      if (acc !== -1) return acc;
      if (!el) return -1;
      return el.getBoundingClientRect().top <= 120 ? i : -1;
    }, -1);
    if (idx !== -1) setActive(idx);
  }, [scrollY]);
  useEffect(() => {
    const el = tabRefs.current[active];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [active]);
  return (
    <nav className="nav-secondary">
      {anchors.map(([id, label], i) => (
        <a
          key={id}
          ref={(el) => (tabRefs.current[i] = el)}
          href={id}
          className={i === active ? "active" : ""}
          onClick={() => setActive(i)}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

function HighlightsSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const cards = [
    {
      tag: "Premium Quality",
      title: "Grade A+\nFresh Produce.",
      desc: "Every item is carefully selected at the peak of freshness. Consistent quality standards applied at every stage — from farm to your kitchen.",
      img: "/assets/images/home/1.jpeg",
      col: "#163d1f",
      stat: "A+",
      statLbl: "Quality Grade",
      icon: "bi-award",
    },
    {
      tag: "Timely Delivery",
      title: "On-Time.\nEvery Time.",
      desc: "Our UAE-wide distribution network ensures prompt, reliable delivery to your business every single day. No delays, no compromises.",
      img: "/assets/images/home/2.jpeg",
      col: "#2a0f0f",
      stat: "99%",
      statLbl: "On-Time Delivery",
      icon: "bi-truck",
    },
    {
      tag: "Food Safety Assured",
      title: "HACCP & ISO\nCertified.",
      desc: "We operate under internationally recognised food safety standards — HACCP, ISO 9001, ISO 22000 — providing full traceability from source to delivery.",
      img: "/assets/images/latest/1.png",
      col: "#1a1a2e",
      stat: "3x",
      statLbl: "Certified Standards",
      icon: "bi-shield-check",
    },
    {
      tag: "Global Sourcing",
      title: "Sourced from\n15+ Countries.",
      desc: "Our international sourcing network spans India, Philippines, Brazil, Spain, South Africa and more — guaranteeing year-round availability of every product.",
      img: "/assets/images/home/3.png",
      col: "#0a1e38",
      stat: "15+",
      statLbl: "Source Countries",
      icon: "bi-globe-europe-africa",
    },
    {
      tag: "Customer Focused",
      title: "20+ Years\nof Excellence.",
      desc: "Since 2002, we have built long-term partnerships with the UAE's leading hotels, restaurants, catering groups and retail chains through unmatched service.",
      img: "/assets/images/home/8.png",
      col: "#3a1e00",
      stat: "20+",
      statLbl: "Years of Experience",
      icon: "bi-handshake",
    },
  ];
  const ci = Math.min(cards.length - 1, Math.floor(prog * cards.length));
  const card = cards[ci];
  return (
    <section
      id="highlights"
      ref={wrapRef}
      style={{
        height: `${100 * (cards.length + 1.5)}vh`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          overflow: "hidden",
          background: "#090909",
        }}
      >
        {cards.map((c, i) => (
          <img
            key={i}
            src={c.img}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.8) saturate(1.1)",
              opacity: i === ci ? 1 : 0,
              transition: "opacity 0.75s ease",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg,${card.col}bb 0%,transparent 65%)`,
            transition: "background 0.7s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to left,rgba(0,0,0,0.7) 0%,transparent 60%)",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ maxWidth: 520, color: "#fff" }}>
            <div
              className="sl"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <span style={{ fontSize: 18 }}>
                <i className={`bi ${card.icon}`}></i>
              </span>
              {card.tag}
            </div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(36px,4.8vw,50px)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: -2,
                marginBottom: 22,
                whiteSpace: "pre-line",
                transition: "opacity 0.4s",
              }}
            >
              {card.title}
            </h2>
            <p
              style={{
                fontSize: 17,
                lineHeight: 1.3,
                color: "rgba(255,255,255,0.72)",
                marginBottom: 44,
              }}
            >
              {card.desc}
            </p>
            <div style={{ marginBottom: 48 }}>
              <div
                style={{
                  fontSize: 64,
                  fontWeight: 900,
                  fontFamily: "Georgia,serif",
                  color: "#dd3c30",
                  lineHeight: 1,
                  animation: "countUp 0.5s ease both",
                }}
              >
                {card.stat}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.45)",
                  marginTop: 6,
                  letterSpacing: 0.5,
                }}
              >
                {card.statLbl}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {cards.map((_, i) => (
                <div
                  key={i}
                  className={`progress-pill${i === ci ? " active" : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.1)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function ProductsSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const isMobile = useIsMobile();
  const panels = [
    {
      tag: "Pineapple — Dole Tropical",
      title: "Golden.\nSweet. Fresh.",
      sub: "Philippines · Premium Export Grade",
      body: "Dole Tropical pineapples are sourced from the rich, fertile soils of Costa Rica — cultivated to meet the highest international standards for sweetness and quality. Vibrant golden colour, consistent sizing, perfect brix every time.",
      stat: "Brix 14+",
      statLbl: "Sweetness Index",
      img: "/assets/images/home/16.jpeg",
      bg: "#0d0909",
    },
    {
      tag: "Banana — Dole Tropical",
      title: "Boost Your\nImmune System.",
      sub: "Vitamins B6, C · Fiber · Potassium",
      body: "Bananas are naturally sweet, creamy, and packed with energy. Perfectly ripened for flavour, they're a versatile addition to smoothies, baked goods, or breakfast bowls. Bananas help fight foodborne illnesses by fortifying protective gut bacteria.",
      stat: "105",
      statLbl: "Calories per serving",
      img: "/assets/images/home/17.jpg",
      bg: "#080808",
    },
  ];
  const ci = Math.min(panels.length - 1, Math.floor(prog * panels.length));
  const panel = panels[ci];
  return (
    <section
      id="products"
      ref={wrapRef}
      style={{ height: `${100 * (panels.length + 1)}vh`, position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: panel.bg,
          overflow: "hidden",
          transition: "background 0.8s ease",
        }}
      >
        {panels.map((p, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              inset: 0,
              opacity: i === ci ? 1 : 0,
              transition: "opacity 0.8s ease",
            }}
          >
            <img
              src={p.img}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "brightness(0.8) saturate(1.2)",
              }}
            />
          </div>
        ))}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg,rgba(0, 0, 0, 0.23) 0%,rgba(0, 0, 0, 0.19) 55%,transparent 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "-5%",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <span
            className="serif"
            style={{
              fontSize: "clamp(160px,22vw,320px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.03)",
              lineHeight: 1,
            }}
          >
            {ci + 1}
          </span>
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              marginRight: "auto",
              position: "relative",
              width: isMobile ? "0%" : "40%",
              flexShrink: 0,
            }}
          ></div>
          <div style={{ maxWidth: 540, color: "#fff", width: "100%" }}>
            <div className="sl">{panel.tag}</div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(36px,4.8vw,58px)",
                fontWeight: 800,
                lineHeight: 1.06,
                letterSpacing: -2,
                marginBottom: 24,
                whiteSpace: "pre-line",
              }}
            >
              {panel.title}
            </h2>
            <div
              style={{
                fontSize: 13,
                color: "#dd3c30",
                fontWeight: 600,
                letterSpacing: 0.5,
                marginBottom: 20,
                padding: "8px 16px",
                background: "rgba(221,60,48,0.1)",
                borderRadius: 8,
                display: "inline-block",
                border: "1px solid rgba(221,60,48,0.2)",
              }}
            >
              {panel.sub}
            </div>
            <p
              style={{
                fontSize: 16,
                lineHeight: 1.3,
                color: "rgba(255,255,255,0.7)",
                marginBottom: 40,
                maxWidth: 440,
              }}
            >
              {panel.body}
            </p>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
              <div
                style={{
                  fontSize: 68,
                  fontWeight: 900,
                  fontFamily: "Georgia,serif",
                  color: "#dd3c30",
                  lineHeight: 1,
                }}
              >
                {panel.stat}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.5)",
                  maxWidth: 120,
                  lineHeight: 1.4,
                }}
              >
                {panel.statLbl}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 36 }}>
              {panels.map((_, i) => (
                <div
                  key={i}
                  className={`progress-pill${i === ci ? " active" : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function FreshnessSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const steps = [
    {
      title: "Reliable Delivery",
      sub: "UAE-Wide Distribution",
      body: "Efficient daily delivery across the UAE. Our dedicated fleet and logistics team ensure on-time arrival at your hotel, restaurant, or catering facility — every single day.",
      pct: 0,
    },
    {
      title: "Premium Quality",
      sub: "Consistent Grade Standards",
      body: "Carefully selected fresh produce with consistent quality standards. Every item inspected at source and checked again before dispatch — because quality comes first.",
      pct: 0.33,
    },
    {
      title: "Cold Chain Excellence",
      sub: "Temperature-Controlled",
      body: "Advanced cold storage systems maintaining optimal conditions to preserve freshness and quality. Proper storage and handling from farm to your kitchen.",
      pct: 0.67,
    },
  ];
  const step = steps.reduce((a, s) => (prog >= s.pct ? s : a), steps[0]);
  const imgScale = 1 + prog * 0.18;
  return (
    <section
      id="freshness"
      ref={wrapRef}
      style={{ height: "400vh", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: "#111",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: `translate(-50%,-50%) scale(${0.85 + prog * 0.15})`,
            transition: "transform 0.05s linear",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <span
            className="serif"
            style={{
              fontSize: "clamp(80px,14vw,200px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.04)",
              letterSpacing: -8,
              whiteSpace: "nowrap",
              lineHeight: 1,
            }}
          >
            Freshness
          </span>
        </div>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <img
            src="/assets/images/latest/3.png"
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.7) saturate(1.1)",
              transform: `scale(${imgScale})`,
              transition: "transform 0.05s linear",
              transformOrigin: "center center",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to right,rgba(0,0,0,0.78) 0%,transparent 55%)",
            }}
          />
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            width: "100%",
            padding: "0 min(64px,6.4vw)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div style={{ maxWidth: 800, color: "#fff" }}>
            {steps.map((s, i) => {
              const active = step.title === s.title;
              return (
                <div
                  key={i}
                  style={{
                    marginBottom: 32,
                    opacity: active ? 1 : 0.28,
                    transform: active ? "translateX(0)" : "translateX(-16px)",
                    transition: "all 0.5s ease",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#dd3c30",
                      letterSpacing: 2.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    {s.sub}
                  </div>
                  <h3
                    className="serif"
                    style={{
                      fontSize: "clamp(26px,3.2vw,44px)",
                      fontWeight: 800,
                      lineHeight: 1.12,
                      letterSpacing: -1,
                      marginBottom: 12,
                    }}
                  >
                    {s.title}
                  </h3>
                  {active && (
                    <p
                      style={{
                        fontSize: 16,
                        lineHeight: 1.3,
                        color: "rgba(255,255,255,0.68)",
                        maxWidth: 420,
                      }}
                    >
                      {s.body}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            right: 48,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            zIndex: 3,
          }}
        >
          {steps.map((s, i) => (
            <div
              key={i}
              style={{
                width: 3,
                height: step.title === s.title ? 48 : 18,
                borderRadius: 2,
                background:
                  step.title === s.title ? "#dd3c30" : "rgba(255,255,255,0.25)",
                transition: "all 0.4s ease",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── UPDATED: VarietySection with per-card lightbox image slider ─── */
// function VarietySection() {
//   const ref = useRef(null);
//   const vis = useInView(ref);
//   const [hov, setHov] = useState(null);
//   const [lightbox, setLightbox] = useState(null); // index of active item, or null
//   const [slideIdx, setSlideIdx] = useState(0);

//   const items = [
//     {
//       name: "Fresh Fruits",
//       region: "Global Sourcing",
//       season: "Year-Round",
//       img: IMG.freshProduce,
//       images: [IMG.freshProduce, IMG.pineappleDole, IMG.bananaDole],
//       price: "Premium Grade",
//       large: true,
//     },
//     {
//       name: "Pineapple — Dole",
//       region: "Philippines",
//       season: "Year-Round",
//       img: IMG.pineappleOla2,
//       images: [IMG.pineappleOla2, IMG.new19, IMG.new20],
//       price: "Export Grade",
//     },
//     {
//       name: "Banana — Terioka",
//       region: "Ecuador",
//       season: "Year-Round",
//       img: IMG.bananaDole,
//       images: [IMG.bananaDole, IMG.pineappleOla],
//       price: "A+ Grade",
//     },
//     {
//       name: "Banana — Dole",
//       region: "Philippines / Ecuador",
//       season: "Year-Round",
//       img: IMG.pineappleOla,
//       images: [IMG.pineappleOla, IMG.bananaDole],
//       price: "Export Grade",
//       large: true,
//     },
//     {
//       name: "Fresh Vegetables",
//       region: "Spain · Egypt · India",
//       season: "Year-Round",
//       img: IMG.contact,
//       images: [IMG.contact, IMG.customers, IMG.bananaTerioka],
//       price: "HORECA Grade",
//     },
//     {
//       name: "Exotic & Imported",
//       region: "Global Network",
//       season: "Seasonal",
//       img: IMG.globalNetwork,
//       images: [
//         IMG.globalNetwork,
//         IMG.whoWeAre,
//         IMG.pineappleDole,
//         IMG.warehouse,
//       ],
//       price: "Specialty",
//     },
//   ];

//   const openLightbox = (i) => {
//     setLightbox(i);
//     setSlideIdx(0);
//   };

//   const activeItem = lightbox !== null ? items[lightbox] : null;

//   return (
//     <section
//       id="variety"
//       ref={ref}
//       style={{ background: "#f6f2ed", padding: "min(80px,8vw) 24px" }}
//     >
//       <div style={{ maxWidth: 1312, margin: "0 auto" }}>
//         <div
//           style={{
//             textAlign: "center",
//             marginBottom: 50,
//             opacity: vis ? 1 : 0,
//             transform: vis ? "translateY(0)" : "translateY(32px)",
//             transition: "all 0.85s ease",
//           }}
//         >
//           <div className="sl-dark">Fresh Produce We Supply</div>
//           <h2
//             className="serif"
//             style={{
//               fontSize: "clamp(40px,5.5vw,55px)",
//               fontWeight: 800,
//               letterSpacing: -2.5,
//               color: "#1a1a1a",
//               lineHeight: 1.05,
//               marginBottom: 16,
//             }}
//           >
//             Fresh Fruits &amp;{" "}
//             <span style={{ color: "#276530" }}>Vegetables</span>
//           </h2>
//           <p
//             style={{
//               fontSize: 17,
//               color: "#666",
//               maxWidth: 700,
//               margin: "0 auto",
//               lineHeight: 1.3,
//             }}
//           >
//             A wide variety sourced locally and globally — ensuring consistent
//             quality, freshness and availability to meet the dynamic needs of our
//             HORECA clients.
//           </p>
//         </div>
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(4,1fr)",
//             gridAutoRows: "280px",
//             gap: 3,
//           }}
//         >
//           {items.map((it, i) => (
//             <div
//               key={i}
//               onMouseEnter={() => setHov(i)}
//               onMouseLeave={() => setHov(null)}
//               onClick={() => openLightbox(i)}
//               style={{
//                 gridColumn: it.large ? "span 2" : "span 1",
//                 position: "relative",
//                 overflow: "hidden",
//                 cursor: "zoom-in",
//                 borderRadius: 4,
//                 opacity: vis ? 1 : 0,
//                 transform: vis ? "translateY(0)" : "translateY(40px)",
//                 transition: `opacity 0.7s ease ${Math.min(i, 5) * 0.09}s,transform 0.7s ease ${Math.min(i, 5) * 0.09}s`,
//               }}
//             >
//               <img
//                 src={it.img}
//                 alt={it.name}
//                 style={{
//                   width: "100%",
//                   height: "100%",
//                   objectFit: "cover",
//                   transform: hov === i ? "scale(1.07)" : "scale(1)",
//                   transition: "transform 0.65s cubic-bezier(.22,1,.36,1)",
//                 }}
//               />
//               <div
//                 style={{
//                   position: "absolute",
//                   inset: 0,
//                   background:
//                     hov === i
//                       ? "linear-gradient(to top,rgba(0,0,0,0.87) 0%,rgba(0,0,0,0.25) 55%,transparent 100%)"
//                       : "linear-gradient(to top,rgba(0,0,0,0.68) 0%,transparent 55%)",
//                   transition: "background 0.4s ease",
//                 }}
//               />
//               <div
//                 style={{
//                   position: "absolute",
//                   bottom: 0,
//                   left: 0,
//                   right: 0,
//                   padding: 22,
//                   color: "#fff",
//                 }}
//               >
//                 <div
//                   style={{
//                     fontSize: 10,
//                     color: "#dd3c30",
//                     fontWeight: 700,
//                     letterSpacing: 2,
//                     textTransform: "uppercase",
//                     marginBottom: 5,
//                   }}
//                 >
//                   {it.season}
//                 </div>
//                 <div
//                   style={{
//                     fontSize: 18,
//                     fontWeight: 800,
//                     fontFamily: "Georgia,serif",
//                   }}
//                 >
//                   {it.name}
//                 </div>
//                 <div
//                   style={{
//                     display: "flex",
//                     justifyContent: "space-between",
//                     alignItems: "center",
//                     maxHeight: hov === i ? 44 : 0,
//                     overflow: "hidden",
//                     transition: "max-height 0.4s ease",
//                     marginTop: 4,
//                   }}
//                 >
//                   <span
//                     style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}
//                   >
//                     {it.region}
//                   </span>
//                   <span
//                     style={{
//                       background: "#276530",
//                       padding: "3px 12px",
//                       borderRadius: 12,
//                       fontSize: 13,
//                       fontWeight: 700,
//                     }}
//                   >
//                     {it.price}
//                   </span>
//                 </div>
//               </div>
//               {hov === i && (
//                 <div
//                   style={{
//                     position: "absolute",
//                     top: 12,
//                     right: 12,
//                     background: "rgba(0,0,0,0.4)",
//                     borderRadius: 5,
//                     padding: "3px 7px",
//                     fontSize: 11,
//                     color: "rgba(255,255,255,0.7)",
//                   }}
//                 >
//                   ⊕
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       {lightbox !== null && activeItem && (
//         <div
//           onClick={() => setLightbox(null)}
//           style={{
//             position: "fixed",
//             inset: 0,
//             zIndex: 9999,
//             background: "rgba(0,0,0,0.92)",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             padding: 24,
//             animation: "popIn 0.3s cubic-bezier(.22,1,.36,1) both",
//           }}
//         >
//           <div
//             onClick={(e) => e.stopPropagation()}
//             style={{
//               position: "relative",
//               maxWidth: 900,
//               width: "100%",
//               maxHeight: "85vh",
//               display: "flex",
//               flexDirection: "column",
//               alignItems: "center",
//             }}
//           >
//             <img
//               src={activeItem.images[slideIdx]}
//               alt={activeItem.name}
//               style={{
//                 maxWidth: "100%",
//                 maxHeight: "70vh",
//                 objectFit: "contain",
//                 borderRadius: 16,
//                 boxShadow: "0 40px 120px rgba(0,0,0,0.8)",
//               }}
//             />
//             <div style={{ marginTop: 18, textAlign: "center", color: "#fff" }}>
//               <div style={{ fontSize: 18, fontWeight: 700 }}>
//                 {activeItem.name}
//               </div>
//               <div
//                 style={{
//                   fontSize: 13,
//                   color: "rgba(255,255,255,0.5)",
//                   marginTop: 4,
//                 }}
//               >
//                 {activeItem.region} &nbsp;·&nbsp; {activeItem.price}
//               </div>
//             </div>
//             <div
//               style={{
//                 display: "flex",
//                 gap: 8,
//                 marginTop: 18,
//                 justifyContent: "center",
//               }}
//             >
//               {activeItem.images.map((_, si) => (
//                 <div
//                   key={si}
//                   onClick={(e) => {
//                     e.stopPropagation();
//                     setSlideIdx(si);
//                   }}
//                   style={{
//                     width: si === slideIdx ? 24 : 7,
//                     height: 7,
//                     borderRadius: 4,
//                     background:
//                       si === slideIdx ? "#dd3c30" : "rgba(255,255,255,0.4)",
//                     transition: "all 0.3s ease",
//                     cursor: "pointer",
//                   }}
//                 />
//               ))}
//             </div>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 const len = activeItem.images.length;
//                 setSlideIdx((s) => (s - 1 + len) % len);
//               }}
//               style={{
//                 position: "absolute",
//                 left: -60,
//                 top: "40%",
//                 background: "rgba(255,255,255,0.12)",
//                 border: "none",
//                 color: "#fff",
//                 width: 44,
//                 height: 44,
//                 borderRadius: "50%",
//                 cursor: "pointer",
//                 fontSize: 20,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//               }}
//             >
//               ‹
//             </button>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 const len = activeItem.images.length;
//                 setSlideIdx((s) => (s + 1) % len);
//               }}
//               style={{
//                 position: "absolute",
//                 right: -60,
//                 top: "40%",
//                 background: "rgba(255,255,255,0.12)",
//                 border: "none",
//                 color: "#fff",
//                 width: 44,
//                 height: 44,
//                 borderRadius: "50%",
//                 cursor: "pointer",
//                 fontSize: 20,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//               }}
//             >
//               ›
//             </button>
//             <button
//               onClick={() => setLightbox(null)}
//               style={{
//                 position: "absolute",
//                 top: -14,
//                 right: -14,
//                 background: "rgba(255,255,255,0.18)",
//                 border: "none",
//                 color: "#fff",
//                 borderRadius: "50%",
//                 width: 36,
//                 height: 36,
//                 cursor: "pointer",
//                 fontSize: 18,
//                 display: "flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//               }}
//             >
//               <i className="bi bi-x-lg"></i>
//             </button>
//           </div>
//         </div>
//       )}
//     </section>
//   );
// }

function SupplyChainSection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const sRef = useRef(null);
  const prog = useStickyProgress(sRef);
  const imgScale = 1.3 - prog * 0.3;
  return (
    <section
      id="supply"
      ref={sRef}
      style={{ height: "300vh", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: "#000",
          overflow: "hidden",
        }}
      >
        <div
          ref={ref}
          style={{ position: "absolute", inset: 0, overflow: "hidden" }}
        >
          <img
            src="/assets/images/latest/4.png"
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: `scale(${imgScale})`,
              transformOrigin: "center 40%",
              transition: "transform 0.06s linear",
              filter: "brightness(0.5) saturate(1.1)",
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <span
            className="serif"
            style={{
              fontSize: "clamp(100px,18vw,240px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.04)",
              letterSpacing: -10,
              whiteSpace: "nowrap",
            }}
          >
            Supply
          </span>
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            color: "#fff",
            padding: "0 min(40px,4vw)",
          }}
        >
          <div
            style={{
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(28px)",
              transition: "all 0.8s ease",
            }}
          >
            <div className="sl">Supply Chain & Logistics Excellence</div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px,5vw,60px)",
                fontWeight: 800,
                letterSpacing: -2,
                lineHeight: 1.07,
                marginBottom: 20,
              }}
            >
              Global Sourcing.{" "}
              <span style={{ color: "#dd3c30" }}>Local Excellence.</span>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "rgba(255,255,255,0.65)",
                maxWidth: 900,
                margin: "0 auto min(52px,5.2vw)",
                lineHeight: 1.3,
              }}
            >
              Our efficient supply chain is designed to ensure freshness,
              quality, and reliability at every stage. From global sourcing to
              final delivery, we maintain strict control to meet the highest
              standards of the HORECA industry.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
              gap: 8,
              maxWidth: 760,
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(28px)",
              transition: "all 0.85s ease 0.15s",
            }}
          >
            {[
              ["Global", "Sourcing"],
              ["Quality", "Control"],
              ["Storage &", "Handling"],
              ["UAE-Wide", "Distribution"],
            ].map(([v, l], i) => (
              <div
                key={i}
                style={{
                  background: "rgba(255,255,255,0.07)",
                  borderRadius: 16,
                  padding: "22px 20px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <i
                  className={
                    [
                      "bi bi-globe-europe-africa",
                      "bi bi-check-circle-fill",
                      "bi bi-snow",
                      "bi bi-truck",
                    ][i]
                  }
                  style={{
                    fontSize: 28,
                    marginBottom: 8,
                    display: "block",
                    color: "#dd3c30",
                  }}
                ></i>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#dd3c30",
                    lineHeight: 1.2,
                  }}
                >
                  {v}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "rgba(255,255,255,0.5)",
                    marginTop: 4,
                  }}
                >
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GlobalNetworkSection() {
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const ref = useRef(null);
  const vis = useInView(ref);
  const regions = [
    {
      label: "15+ Countries",
      img: "/assets/images/latest/6.png",
    },
  ];
  const ci = Math.min(regions.length - 1, Math.floor(prog * regions.length));
  return (
    <section
      id="global"
      ref={wrapRef}
      style={{ height: "320vh", position: "relative" }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: "#060606",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            width: `${regions.length * 100}%`,
            transform: `translateX(${((ci / regions.length) * -100) / 1}%)`,
            transition: "transform 0.05s linear",
          }}
        >
          {regions.map((s, i) => (
            <div
              key={i}
              style={{
                width: `${100 / regions.length}%`,
                height: "100%",
                flexShrink: 0,
                position: "relative",
              }}
            >
              <img
                src={s.img}
                alt={s.label}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "brightness(0.8) saturate(1.1)",
                }}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right,rgba(0,0,0,0.85) 0%,rgba(0,0,0,0.3) 60%,transparent 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -20,
            left: 0,
            right: 0,
            textAlign: "center",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <span
            className="serif"
            style={{
              fontSize: "clamp(80px,16vw,240px)",
              fontWeight: 900,
              color: "rgba(255,255,255,0.03)",
              letterSpacing: -8,
              lineHeight: 1,
            }}
          >
            Global
          </span>
        </div>
        <div
          ref={ref}
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1312,
            margin: "0 auto",
            padding: "0 min(64px,6.4vw)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div className="sl">Our Global Sourcing Network</div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(36px,5vw,55px)",
              fontWeight: 800,
              letterSpacing: -2.5,
              color: "#fff",
              lineHeight: 1.06,
              marginBottom: 24,
              maxWidth: 600,
            }}
          >
            World's Finest Fruits.
            <br />
            <span style={{ color: "#dd3c30" }}>UAE Delivered.</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.3,
              color: "rgba(255,255,255,0.65)",
              maxWidth: 440,
              marginBottom: 48,
            }}
          >
            We source fresh fruits and vegetables from leading agricultural
            regions worldwide, ensuring consistent quality, year-round
            availability, and competitive pricing for our HORECA partners.
          </p>
          <div style={{ display: "flex", gap: 12, marginBottom: 32 }}>
            {regions.map((s, i) => (
              <div
                key={i}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  background:
                    i === ci ? "rgba(221,60,48,0.2)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${i === ci ? "rgba(221,60,48,0.5)" : "rgba(255,255,255,0.1)"}`,
                  transition: "all 0.3s",
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: i === ci ? "#dd3c30" : "rgba(255,255,255,0.6)",
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.4)",
                    marginTop: 3,
                  }}
                >
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}

function CustomersSection() {
  const ref = useRef(null);
  const vis = useInView(ref);

  // All 35 client logos, matched to /assets/images/company/client/{n}.png
  const clients = [
    { n: 1, name: "Landmark Hotel" },
    { n: 2, name: "Rove Hotels" },
    { n: 3, name: "New Horizon Catering Services" },
    { n: 4, name: "ADNH Compass" },
    { n: 5, name: "NCT&H" },
    { n: 6, name: "Valiant" },
    { n: 7, name: "Tamween" },
    { n: 8, name: "Emirates Co-op" },
    { n: 9, name: "Wrist Ship Supply" },
    { n: 10, name: "Dusit Hotels & Resorts" },
    { n: 11, name: "Royal M Hotel" },
    { n: 12, name: "Chili's" },
    { n: 13, name: "Americana Quality" },
    { n: 14, name: "KFC" },
    { n: 15, name: "Atlantis Dubai" },
    { n: 16, name: "The Imperial Group Hotels & Apartments" },
    { n: 17, name: "The Coffee Club" },
    { n: 18, name: "Pizza Hut" },
    { n: 19, name: "TGI Friday's" },
    { n: 20, name: "Riviera Hotel" },
    { n: 21, name: "Al Naboodah" },
    { n: 22, name: "The Emirates Group" },
    { n: 23, name: "APEX National Catering" },
    { n: 24, name: "Transguard Group" },
    { n: 25, name: "Shakespeare" },
    { n: 26, name: "Carrefour" },
    { n: 27, name: "SevenSeas" },
    { n: 28, name: "Al Zahra Hospital Dubai" },
    { n: 29, name: "National Catering Services & Foodstuff" },
    { n: 30, name: "Four N Catering" },
    { n: 31, name: "Ten N Tale" },
    { n: 32, name: "Tea Talk Filli" },
    { n: 33, name: "Belhasa Restaurant Management" },
    { n: 34, name: "Slices School Food Experts" },
    { n: 35, name: "Al Masaood" },
  ];

  const mid = Math.ceil(clients.length / 2);
  const rowA = clients.slice(0, mid);
  const rowB = clients.slice(mid);

  const LogoCard = ({ c }) => (
    <div
      title={c.name}
      style={{
        flexShrink: 0,
        width: 230,
        height: 120,
        margin: "0 8px",
        background: "#fff",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 14px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
      }}
      className="fg-client-logo-card"
    >
      <img
        src={`/assets/images/company/client/${c.n}.png`}
        alt={c.name}
        loading="lazy"
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      />
    </div>
  );

  return (
    <section
      id="customers"
      ref={ref}
      style={{
        background: "#0d0a0a",
        padding: "min(110px,11vw) 0",
        overflow: "hidden",
      }}
    >
      {/* Scoped styles — only used by this section, no external CSS needed */}
      <style>{`
        @keyframes fgMarqueeLeft { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes fgMarqueeRight { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        .fg-marquee-track { display: flex; width: max-content; animation: fgMarqueeLeft 38s linear infinite; }
        .fg-marquee-track.fg-reverse { animation: fgMarqueeRight 50s linear infinite; }
        .fg-marquee-row:hover .fg-marquee-track { animation-play-state: paused; }
      `}</style>

      <div style={{ maxWidth: 1312, margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{
            textAlign: "center",
            color: "#fff",
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(32px)",
            transition: "all 0.85s ease",
            marginBottom: 56,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 3,
              color: "#dd3c30",
              fontWeight: 700,
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Our Valued Customers
          </div>
          <h2
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "clamp(38px,5vw,55px)",
              fontWeight: 800,
              letterSpacing: -2.5,
              lineHeight: 1.05,
              marginBottom: 16,
            }}
          >
            Trusted by the UAE's
            <br />
            <span style={{ color: "#dd3c30" }}>Leading Brands.</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.58)",
              maxWidth: 600,
              margin: "0 auto",
              lineHeight: 1.3,
            }}
          >
            At Fruits Garden Trading LLC, our customers are at the heart of
            everything we do. We are committed to building long-term
            partnerships by consistently delivering quality, reliability, and
            exceptional service.
          </p>
        </div>
      </div>

      {/* Row 1 — scrolls left, duplicated for a seamless infinite loop */}
      <div
        className="fg-marquee-row"
        style={{
          position: "relative",
          marginBottom: 18,
          maskImage:
            "linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)",
        }}
      >
        <div className="fg-marquee-track">
          {[...rowA, ...rowA].map((c, i) => (
            <LogoCard c={c} key={`a-${c.n}-${i}`} />
          ))}
        </div>
      </div>

      {/* Row 2 — scrolls right, opposite direction for visual rhythm */}
      <div
        className="fg-marquee-row"
        style={{
          position: "relative",
          maskImage:
            "linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, #000 6%, #000 94%, transparent 100%)",
        }}
      >
        <div className="fg-marquee-track fg-reverse">
          {[...rowB, ...rowB].map((c, i) => (
            <LogoCard c={c} key={`b-${c.n}-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

// function StrengthSection() {
//   const wrapRef = useRef(null);
//   const prog = useStickyProgress(wrapRef);
//   const ref = useRef(null);
//   const vis = useInView(ref, 0.05);
//   const pillars = [
//     {
//       name: "Integrated Supply Chain",
//       col: "#e8f5e9",
//       accent: "#276530",
//       img: IMG.pineappleDole,
//       desc: "Seamless coordination across sourcing, storage, and distribution to ensure consistent product flow.",
//       sub: "End-to-end operational excellence",
//       price: "Core Service",
//       wt: "Operations",
//     },
//     {
//       name: "Temperature-Controlled Storage",
//       col: "#e3f2fd",
//       accent: "#0d47a1",
//       img: IMG.warehouse,
//       desc: "Advanced cold storage systems maintaining optimal conditions to preserve freshness and quality.",
//       sub: "Cold-chain management",
//       price: "24/7 Active",
//       wt: "Storage",
//     },
//     {
//       name: "Precision Sorting & Packing",
//       col: "#fff3e0",
//       accent: "#e65100",
//       img: IMG.supplyChain,
//       desc: "Careful grading and customized packing to meet specific HORECA requirements. Every order exactly right.",
//       sub: "HORECA-grade packaging",
//       price: "Custom",
//       wt: "Packing",
//     },
//     {
//       name: "Efficient Logistics & Distribution",
//       col: "#fce4ec",
//       accent: "#880e4f",
//       img: IMG.globalNetwork,
//       desc: "Streamlined dispatch and a reliable fleet ensuring timely deliveries across the UAE every day.",
//       sub: "UAE-wide distribution network",
//       price: "On-Time",
//       wt: "Delivery",
//     },
//   ];
//   const scrollSel = Math.min(
//     pillars.length - 1,
//     Math.floor(prog * pillars.length),
//   );
//   const sel = scrollSel;
//   const b = pillars[sel];
//   return (
//     <section
//       id="strength"
//       ref={wrapRef}
//       style={{
//         height: `${100 * (pillars.length + 1.5)}vh`,
//         position: "relative",
//       }}
//     >
//       <div
//         style={{
//           position: "sticky",
//           top: 104,
//           height: "calc(100vh - 104px)",
//           background: "#fff",
//           overflow: "hidden",
//         }}
//       >
//         <div
//           style={{
//             position: "absolute",
//             right: "-2%",
//             top: "50%",
//             transform: "translateY(-50%)",
//             pointerEvents: "none",
//             zIndex: 1,
//           }}
//         >
//           <span
//             className="serif"
//             style={{
//               fontSize: "clamp(180px,26vw,380px)",
//               fontWeight: 900,
//               color: "rgba(0,0,0,0.03)",
//               lineHeight: 1,
//             }}
//           >
//             {sel + 1}
//           </span>
//         </div>
//         <div
//           ref={ref}
//           style={{
//             maxWidth: 1312,
//             margin: "0 auto",
//             padding: "0 min(64px,6.4vw)",
//             height: "100%",
//             display: "flex",
//             flexDirection: "column",
//             justifyContent: "center",
//           }}
//         >
//           <div
//             style={{
//               display: "flex",
//               gap: 40,
//               alignItems: "center",
//               flex: 1,
//               maxHeight: 520,
//             }}
//           >
//             <div
//               style={{
//                 flex: 1,
//                 display: "flex",
//                 flexDirection: "column",
//                 gap: 10,
//               }}
//             >
//               {pillars.map((bk, i) => {
//                 const cardProgress = clamp(
//                   prog * pillars.length - i + 0.2,
//                   0,
//                   1,
//                 );
//                 const isActive = i === sel;
//                 return (
//                   <div
//                     key={i}
//                     style={{
//                       padding: "20px 24px",
//                       borderRadius: 18,
//                       background: isActive ? bk.col : "transparent",
//                       border: `1.5px solid ${isActive ? bk.accent + "55" : "#e5e5e5"}`,
//                       transform: `translateX(${clamp((1 - cardProgress) * -70, -70, 0)}px)`,
//                       opacity: cardProgress,
//                       transition: "background 0.38s ease, border 0.38s ease",
//                       position: "relative",
//                       overflow: "hidden",
//                     }}
//                   >
//                     {isActive && (
//                       <div
//                         style={{
//                           position: "absolute",
//                           left: 0,
//                           top: 0,
//                           bottom: 0,
//                           width: 4,
//                           background: bk.accent,
//                           borderRadius: "4px 0 0 4px",
//                         }}
//                       />
//                     )}
//                     <div
//                       style={{
//                         display: "flex",
//                         justifyContent: "space-between",
//                         alignItems: "center",
//                       }}
//                     >
//                       <div style={{ flex: 1 }}>
//                         <div
//                           style={{
//                             display: "flex",
//                             alignItems: "center",
//                             gap: 12,
//                           }}
//                         >
//                           <div
//                             style={{
//                               width: 22,
//                               height: 22,
//                               borderRadius: "50%",
//                               background: bk.accent,
//                               flexShrink: 0,
//                               boxShadow: isActive
//                                 ? `0 0 0 4px ${bk.accent}33`
//                                 : "none",
//                               transition: "box-shadow 0.3s",
//                             }}
//                           />
//                           <span
//                             style={{
//                               fontSize: 17,
//                               fontWeight: 600,
//                               letterSpacing: -0.2,
//                               color: isActive ? bk.accent : "#1a1a1a",
//                             }}
//                           >
//                             {bk.name}
//                           </span>
//                           <span
//                             style={{
//                               fontSize: 12,
//                               color: "#999",
//                               marginLeft: "auto",
//                             }}
//                           >
//                             {bk.wt}
//                           </span>
//                         </div>
//                         {isActive && (
//                           <p
//                             style={{
//                               fontSize: 14,
//                               color: "#555",
//                               lineHeight: 1.6,
//                               margin: "10px 0 0 34px",
//                               maxWidth: 360,
//                               animation: "fadeSlideUp 0.4s ease both",
//                             }}
//                           >
//                             {bk.desc}
//                           </p>
//                         )}
//                       </div>
//                       <div
//                         style={{
//                           marginLeft: 16,
//                           fontSize: 14,
//                           fontWeight: 700,
//                           color: isActive ? bk.accent : "#bbb",
//                           transition: "color 0.3s",
//                           padding: "4px 10px",
//                           borderRadius: 8,
//                           background: isActive
//                             ? `${bk.accent}18`
//                             : "transparent",
//                         }}
//                       >
//                         {bk.price}
//                       </div>
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>
//             <div style={{ flex: 0.8, position: "relative" }}>
//               <div
//                 style={{
//                   borderRadius: 28,
//                   overflow: "hidden",
//                   aspectRatio: "1/1",
//                   background: b.col,
//                   transition: "background 0.5s ease",
//                   position: "relative",
//                   boxShadow: "0 30px 80px rgba(0,0,0,0.12)",
//                 }}
//               >
//                 {pillars.map((bk, i) => (
//                   <img
//                     key={i}
//                     src={bk.img}
//                     alt={bk.name}
//                     style={{
//                       position: i === 0 ? "relative" : "absolute",
//                       top: 0,
//                       left: 0,
//                       width: "100%",
//                       height: "100%",
//                       objectFit: "cover",
//                       borderRadius: 28,
//                       opacity: i === sel ? 1 : 0,
//                       transform: i === sel ? "scale(1)" : "scale(1.06)",
//                       transition: "opacity 0.6s ease,transform 0.6s ease",
//                     }}
//                   />
//                 ))}
//                 <div
//                   style={{
//                     position: "absolute",
//                     bottom: 20,
//                     right: 20,
//                     display: "flex",
//                     gap: 6,
//                   }}
//                 >
//                   {pillars.map((_, i) => (
//                     <div
//                       key={i}
//                       style={{
//                         width: i === sel ? 28 : 7,
//                         height: 7,
//                         borderRadius: 4,
//                         background:
//                           i === sel ? b.accent : "rgba(255,255,255,0.5)",
//                         transition: "all 0.4s ease",
//                       }}
//                     />
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//         <div
//           style={{
//             position: "absolute",
//             bottom: 0,
//             left: 0,
//             right: 0,
//             height: 3,
//             background: "rgba(0,0,0,0.06)",
//           }}
//         >
//           <div
//             style={{
//               height: "100%",
//               background: "#276530",
//               width: `${prog * 100}%`,
//               transition: "width 0.1s linear",
//             }}
//           />
//         </div>
//       </div>
//     </section>
//   );
// }

function StatsSection() {
  const ref = useRef(null);
  const vis = useInView(ref);
  const [animPct, setAnimPct] = useState(0);
  const [counts, setCounts] = useState({ years: 0, clients: 0, countries: 0 });
  useEffect(() => {
    if (vis) {
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / 1800, 1);
        setAnimPct(p);
        setCounts({
          years: Math.round(p * 22),
          clients: Math.round(p * 200),
          countries: Math.round(p * 15),
        });
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }, [vis]);
  return (
    <section
      id="strength"
      ref={ref}
      style={{ background: "#fff", padding: "min(60px,6vw) 24px", overflow: "hidden" }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 64,
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(40px)",
            transition: "all 0.9s ease",
          }}
        >
          <div className="sl-dark">Our Strength</div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 48,
              marginBottom: 20,
            }}
          >
            {[
              [counts.years + "+", "Years of Experience"],
              [counts.clients + "+", "HORECA Clients"],
              [counts.countries + "+", "Source Countries"],
            ].map(([v, l], i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  opacity: vis ? 1 : 0,
                  transform: vis ? "translateY(0)" : "translateY(30px)",
                  transition: `all 0.7s ease ${0.15 * i}s`,
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(44px,6vw,72px)",
                    fontWeight: 900,
                    fontFamily: "Georgia,serif",
                    color: "#276530",
                    lineHeight: 1,
                    letterSpacing: -3,
                  }}
                >
                  {v}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#999",
                    marginTop: 6,
                    letterSpacing: 1,
                  }}
                >
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 80,
            alignItems: "center",
            marginBottom: 20,
            opacity: vis ? 1 : 0,
            transform: vis ? "translateY(0)" : "translateY(32px)",
            transition: "all 0.85s ease 0.2s",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 300 }}>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(36px,4.5vw,55px)",
                fontWeight: 800,
                letterSpacing: -2,
                color: "#1a1a1a",
                lineHeight: 1,
                marginBottom: 20,
              }}
            >
              Reliable Delivery.
              <br />
              <span style={{ color: "#276530" }}>Premium Quality Reach.</span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "#555",
                lineHeight: 1.3,
                maxWidth: 420,
                marginBottom: 40,
              }}
            >
              Our UAE-wide distribution network spans every major city and
              emirate. Freshness is measured in consistency — and ours is the
              industry standard.
            </p>
            {[
              ["On-Time Delivery Rate", 99],
              ["Customer Satisfaction", 98],
              ["Quality Consistency", 97],
            ].map(([lbl, pct]) => (
              <div key={lbl} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    color: "#999",
                    marginBottom: 8,
                  }}
                >
                  <span>{lbl}</span>
                  <span style={{ fontWeight: 700, color: "#276530" }}>
                    {Math.round(animPct * pct)}%
                  </span>
                </div>
                <div
                  style={{
                    height: 10,
                    background: "#eee",
                    borderRadius: 5,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 5,
                      background: "linear-gradient(90deg,#276530,#dd3c30)",
                      width: `${animPct * pct}%`,
                      transition: "none",
                      boxShadow:
                        animPct > 0.9 ? "0 0 12px rgba(221,60,48,0.5)" : "none",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 260, position: "relative", aspectRatio: "1/1" }}>
            <div
              style={{ borderRadius: 24, overflow: "hidden", height: "100%" }}
            >
              <img
                src= "/assets/images/home/7.png"
                alt="Our Strength"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
            <div
              style={{
                position: "absolute",
                top: 28,
                right: 28,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#276530",
                animation: "pulseGreen 2s ease-in-out infinite",
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function GallerySection() {
  const isMobile = useIsMobile();
  const ref = useRef(null);
  const vis = useInView(ref);
  const wrapRef = useRef(null);
  const prog = useStickyProgress(wrapRef);
  const [lightbox, setLb] = useState(null);
  const photos = [
    {
      img: "/assets/images/company/1.jpeg",
      label: "Our Headquarters",
      sub: "Dubai Food District, UAE",
      wide: true,
    },
    {
      img: "/assets/images/company/2.jpeg",
      label: "Strength & Supply Excellence",
      sub: "Operations Team",
    },
    {
      img: "/assets/images/company/global.jpg",
      label: "Global Sourcing Network",
      sub: "15+ Source Countries",
    },
  ];
  const getPhotoProgress = (i) => {
    const step = 1 / (photos.length + 1);
    return clamp((prog - i * step * 0.7) / (step * 2), 0, 1);
  };
  return (
    <section
      id="gallery"
      ref={wrapRef}
      style={{
        height: `${100 * (Math.ceil(photos.length / 2) + 3)}vh`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 104,
          height: "calc(100vh - 104px)",
          background: "#0a0a0a",
          overflow: "hidden",
        }}
      >
        <div
          ref={ref}
          style={{
            maxWidth: 1312,
            margin: "0 auto",
            padding: "min(40px,4vw) 32px 0",
            height: "100%",
          }}
        >
          <div
            style={{
              textAlign: "center",
              color: "#fff",
              marginTop: 25,
              marginBottom: 32,
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(32px)",
              transition: "all 0.85s ease",
            }}
          >
            <div className="sl">From Our Operations</div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(36px,5vw,52px)",
                fontWeight: 800,
                letterSpacing: -2.5,
                lineHeight: 1.04,
                marginBottom: 10,
              }}
            >
              Life at <span style={{ color: "#dd3c30" }}>Fruits Garden</span>
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.45)",
                maxWidth: 380,
                margin: "0 auto",
              }}
            >
              A glimpse into our world-class operations, warehouse, and the team
              behind UAE's trusted FFV supplier.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",
              gridAutoRows: "320px",
              gap: 4,
              maxHeight: "calc(100vh - 80px)",
              overflow: "hidden",
            }}
          >
            {photos.map((ph, i) => {
              const p = getPhotoProgress(i);
              return (
                <div
                  key={i}
                  onClick={() => setLb(i)}
                  style={{
                    gridColumn: ph.wide ? "span 2" : "span 1",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 8,
                    cursor: "zoom-in",
                    opacity: p,
                    transform: `scale(${0.88 + p * 0.12}) translateY(${(1 - p) * 30}px)`,
                    transition: "none",
                  }}
                >
                  <img
                    src={ph.img}
                    alt={ph.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: `brightness(${0.55 + p * 0.3})`,
                      transition: "filter 0.3s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.07)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top,rgba(0,0,0,0.7),transparent 50%)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 12,
                      right: 12,
                    }}
                  >
                    <div
                      style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}
                    >
                      {ph.label}
                    </div>
                    <div
                      style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}
                    >
                      {ph.sub}
                    </div>
                  </div>
                  {p > 0.5 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.4)",
                        borderRadius: 5,
                        padding: "3px 7px",
                        fontSize: 11,
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      ⊕
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            style={{
              height: "100%",
              background: "#dd3c30",
              width: `${prog * 100}%`,
              transition: "width 0.1s linear",
            }}
          />
        </div>
      </div>
      {lightbox !== null && (
        <div
          onClick={() => setLb(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            animation: "popIn 0.3s cubic-bezier(.22,1,.36,1) both",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: 900,
              width: "100%",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={photos[lightbox].img}
              alt=""
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 16,
                boxShadow: "0 40px 120px rgba(0,0,0,0.8)",
              }}
            />
            <div style={{ marginTop: 18, textAlign: "center", color: "#fff" }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {photos[lightbox].label}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.5)",
                  marginTop: 4,
                }}
              >
                {photos[lightbox].sub}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLb((l) => (l - 1 + photos.length) % photos.length);
              }}
              style={{
                position: "absolute",
                left: -60,
                top: "40%",
                background: "rgba(255,255,255,0.12)",
                border: "none",
                color: "#fff",
                width: 44,
                height: 44,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ‹
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLb((l) => (l + 1) % photos.length);
              }}
              style={{
                position: "absolute",
                right: -60,
                top: "40%",
                background: "rgba(255,255,255,0.12)",
                border: "none",
                color: "#fff",
                width: 44,
                height: 44,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ›
            </button>
            <button
              onClick={() => setLb(null)}
              style={{
                position: "absolute",
                top: -14,
                right: -14,
                background: "rgba(255,255,255,0.18)",
                border: "none",
                color: "#fff",
                borderRadius: "50%",
                width: 36,
                height: 36,
                cursor: "pointer",
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── NEW: Apple-style Parallax Reveal — Fruit Mosaic Logo ─── */
// function FruitMosaicSection() {
//   const wrapRef = useRef(null);
//   const prog = useStickyProgress(wrapRef);
//   const ref = useRef(null);
//   const vis = useInView(ref, 0.05);

//   // Apple-style: logo scales in, then fades as text reveals
//   const logoScale = 0.7 + prog * 0.55;
//   // Logo: fade IN from 0→0.28, then fade OUT from 0.28→0.55
//   const logoFadeIn = clamp(prog * (1 / 0.28), 0, 1);
//   const logoFadeOut = clamp((prog - 0.28) * (1 / 0.27), 0, 1);
//   const logoOpacity = logoFadeIn * (1 - logoFadeOut);
//   const textOp1 = clamp((prog - 0.3) * 4, 0, 1);
//   const textOp2 = clamp((prog - 0.5) * 4, 0, 1);
//   const textOp3 = clamp((prog - 0.65) * 4, 0, 1);
//   // Dark scrim grows as text appears — ensures text legibility
//   const scrimOp = clamp((prog - 0.25) * 3.5, 0, 0.72);

//   const bgShift = `hsl(${120 + prog * 30},${20 + prog * 15}%,${4 + prog * 3}%)`;

//   return (
//     <section
//       ref={wrapRef}
//       id="fruit-mosaic"
//       style={{ height: "500vh", position: "relative" }}
//     >
//       <div
//         style={{
//           position: "sticky",
//           top: 104,
//           height: "calc(100vh - 104px)",
//           overflow: "hidden",
//           background: bgShift,
//           transition: "background 0.3s linear",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//         }}
//       >
//         {/* Dark scrim — grows as text appears for readability */}
//         <div
//           style={{
//             position: "absolute",
//             inset: 0,
//             background: "#060606",
//             opacity: scrimOp,
//             transition: "opacity 0.05s linear",
//             pointerEvents: "none",
//             zIndex: 1,
//           }}
//         />
//         {/* Ambient glow behind logo */}
//         <div
//           style={{
//             position: "absolute",
//             width: 600,
//             height: 600,
//             borderRadius: "50%",
//             background:
//               "radial-gradient(circle, rgba(221,60,48,0.12) 0%, transparent 70%)",
//             transform: `scale(${0.5 + prog * 1.2})`,
//             transition: "transform 0.05s linear",
//             pointerEvents: "none",
//             zIndex: 0,
//           }}
//         />

//         {/* Central logo image — scales up, then fades as text takes over */}
//         <div
//           ref={ref}
//           style={{
//             position: "absolute",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             width: "100%",
//             height: "100%",
//             zIndex: 0,
//           }}
//         >
//           <img
//             src="/assets/images/latest/8.png"
//             alt="Fruits Garden"
//             onError={(e) => {
//               e.currentTarget.src = "/assets/images/company/5.png";
//             }}
//             style={{
//               width: "100%",
//               height: "auto",
//               objectFit: "contain",
//               transform: `scale(${logoScale})`,
//               opacity: logoOpacity,
//               transition: "transform 0.05s linear, opacity 0.05s linear",
//               filter: `drop-shadow(0 0 ${prog * 60}px rgba(221,60,48,${prog * 0.5}))`,
//             }}
//           />
//         </div>

//         {/* Text layers — sit above scrim, reveal as logo fades */}
//         <div
//           style={{
//             position: "absolute",
//             bottom: "12%",
//             left: "50%",
//             transform: "translateX(-50%)",
//             textAlign: "center",
//             color: "#fff",
//             width: "100%",
//             padding: "0 min(40px,4vw)",
//             pointerEvents: "none",
//             zIndex: 2,
//           }}
//         >
//           <div
//             style={{
//               opacity: textOp1,
//               transform: `translateY(${(1 - textOp1) * 30}px)`,
//               transition: "none",
//               marginBottom: 10,
//             }}
//           >
//             <div
//               className="sl"
//               style={{ textAlign: "center", marginBottom: 8 }}
//             >
//               The Fruits Garden Promise
//             </div>
//           </div>
//           <div
//             style={{
//               opacity: textOp2,
//               transform: `translateY(${(1 - textOp2) * 28}px)`,
//               transition: "none",
//               marginBottom: 10,
//             }}
//           >
//             <h2
//               className="serif"
//               style={{
//                 fontSize: "clamp(32px,4.5vw,56px)",
//                 fontWeight: 800,
//                 letterSpacing: -2,
//                 lineHeight: 1.05,
//                 marginBottom: 0,
//               }}
//             >
//               Every Circle. Every Colour.{" "}
//               <span style={{ color: "#dd3c30" }}>One Vision.</span>
//             </h2>
//           </div>
//           <div
//             style={{
//               opacity: textOp3,
//               transform: `translateY(${(1 - textOp3) * 24}px)`,
//               transition: "none",
//             }}
//           >
//             <p
//               style={{
//                 fontSize: 17,
//                 color: "rgba(255,255,255,0.65)",
//                 maxWidth: 750,
//                 margin: "12px auto 0",
//                 lineHeight: 1.5,
//               }}
//             >
//               From pineapples to pomegranates, cherries to chillies — every
//               fruit and vegetable we supply carries the weight of two decades of
//               quality, care, and commitment to the UAE.
//             </p>
//           </div>
//         </div>

//         {/* Progress bar */}
//         <div
//           style={{
//             position: "absolute",
//             bottom: 0,
//             left: 0,
//             right: 0,
//             height: 3,
//             background: "rgba(255,255,255,0.06)",
//           }}
//         >
//           <div
//             style={{
//               height: "100%",
//               background: "linear-gradient(90deg,#276530,#dd3c30)",
//               width: `${prog * 100}%`,
//               transition: "width 0.1s linear",
//             }}
//           />
//         </div>
//       </div>
//     </section>
//   );
// }

/* ─── UPDATED: StorySection — pure fade animation (no slide/translate) ─── */
function StorySection() {
  const ref = useRef(null);
  const vis = useInView(ref, 0.15);
  const [counts, setCounts] = useState({ years: 0, clients: 0, countries: 0 });
  useEffect(() => {
    if (vis) {
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / 1500, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setCounts({
          years: Math.round(ease * 22),
          clients: Math.round(ease * 200),
          countries: Math.round(ease * 15),
        });
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }, [vis]);

  const stats = [
    { value: "2002", label: "Founded" },
    { value: counts.clients + "+", label: "HORECA Clients" },
    { value: counts.countries + "+", label: "Source Countries" },
  ];

  // Simple, reliable fade-on-scroll: each block just fades opacity in,
  // staggered by a small delay, triggered once when the section enters view.
  const fade = (delay = 0) => ({
    opacity: vis ? 1 : 0,
    transition: `opacity 0.9s ease ${delay}s`,
  });

  return (
    <section
      id="story"
      ref={ref}
      style={{
        background: "#fff",
        padding: "min(80px,8vw) 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <span
          className="serif"
          style={{
            fontSize: "clamp(80px,14vw,200px)",
            fontWeight: 900,
            color: "rgba(0,0,0,0.03)",
            letterSpacing: -8,
            whiteSpace: "nowrap",
          }}
        >
          Story
        </span>
      </div>
      <div
        style={{
          maxWidth: 1312,
          margin: "0 auto",
          display: "flex",
          gap: 80,
          alignItems: "center",
          flexWrap: "wrap",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ flex: 1, minWidth: 320 }}>
          <div
            style={{
              ...fade(0),
              fontSize: 11,
              letterSpacing: 3,
              color: "#276530",
              fontWeight: 700,
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Who We Are
          </div>
          <h2
            className="serif"
            style={{
              ...fade(0.12),
              fontSize: "clamp(36px,4.5vw,35px)",
              fontWeight: 800,
              letterSpacing: -2,
              color: "#1a1a1a",
              lineHeight: 1.08,
              marginBottom: 24,
              whiteSpace: "pre-line",
            }}
          >
            {"Established 2002.\nQuality Comes First."}
          </h2>
          <p
            style={{
              ...fade(0.24),
              fontSize: 16,
              color: "#555",
              lineHeight: 1.3,
              marginBottom: 18,
              maxWidth: 580,
            }}
          >
            Fruits Garden Trading LLC has grown to become one of the leading
            fresh fruits and vegetables (FFV) supply companies in the United
            Arab Emirates. With over two decades of experience, we have built a
            strong reputation for reliability, quality, and excellence in
            serving the HORECA sector.
          </p>
          <p
            style={{
              ...fade(0.36),
              fontSize: 16,
              color: "#555",
              lineHeight: 1.3,
              marginBottom: 18,
              maxWidth: 480,
            }}
          >
            We specialize in the import, export, and wholesale distribution of
            premium fresh produce, sourcing globally from key agricultural
            regions to ensure year-round availability and consistent quality for
            hotels, restaurants, catering companies, and government sectors
            across the UAE.
          </p>
          <div
            style={{
              ...fade(0.48),
              display: "flex",
              gap: 36,
              marginBottom: 8,
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            {stats.map((s, si) => (
              <div key={si}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    fontFamily: "Georgia,serif",
                    color: "#276530",
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                  {s.label}
                </div>
                <div
                  style={{
                    height: 2,
                    background: "#dd3c30",
                    marginTop: 6,
                    width: vis ? "100%" : "0%",
                    transition: "width 0.9s ease 0.6s",
                    borderRadius: 1,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        <div
          style={{ flex: 1, minWidth: 320, position: "relative", ...fade(0.2) }}
        >
          <div
            style={{
              borderRadius: 24,
              overflow: "hidden",
              aspectRatio: "5/5",
              position: "relative",
            }}
          >
            <img
              src="/assets/images/home/15.jpeg"
              alt="Who We Are"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 24,
                left: 24,
                background: "rgba(0,0,0,0.78)",
                backdropFilter: "blur(12px)",
                borderRadius: 14,
                padding: "14px min(40px,4vw)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.12)",
                ...fade(0.55),
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#dd3c30",
                  letterSpacing: 2,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Impact Overview
              </div>
              <div style={{ display: "flex", gap: 40 }}>
                {[
                  ["20+yrs", "Industry Experience"],
                  ["UAE", "Dubai HQ"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 900,
                        fontFamily: "Georgia,serif",
                        color: "#dd3c30",
                      }}
                    >
                      {v}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.5)",
                        marginTop: 2,
                      }}
                    >
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              top: -20,
              right: -20,
              background: "#276530",
              borderRadius: 20,
              padding: "18px 22px",
              color: "#fff",
              textAlign: "center",
              boxShadow: "0 12px 40px rgba(39,101,48,0.35)",
              animation: vis ? "floatY 4s ease-in-out infinite" : "none",
              ...fade(0.4),
            }}
          >
            <div style={{ fontSize: 28 }}>
              <i className="bi bi-trophy"></i>
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                marginTop: 6,
                letterSpacing: 1,
              }}
            >
              TRUSTED
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }}>
              UAE Since 2002
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const isMobile = useIsMobile();
  const cols = [
    {
      h: "Company",
      ls: [
        { label: "Home", path: "/" },
        { label: "About Us", path: "/about-us" },
        { label: "Vision & Mission", path: "/about-us" },
        { label: "Products", path: "/products" },
      ],
    },
    {
      h: "Support",
      ls: [{ label: "Contact Us", path: "/contact-us" }],
    },
    {
      h: "Connect",
      ls: [
        {
          label: "LinkedIn",
          path: "https://linkedin.com/",
          external: true,
        },
        {
          label: "Facebook",
          path: "https://facebook.com/",
          external: true,
        },
        {
          label: "Twitter / X",
          path: "https://x.com/",
          external: true,
        },
        {
          label: "Instagram",
          path: "https://instagram.com/",
          external: true,
        },
        {
          label: "WhatsApp",
          path: "https://wa.me/971565378650",
          external: true,
        },
      ],
    },
  ];

  const linkStyle = {
    color: "rgba(255,255,255,0.45)",
    textDecoration: "none",
    fontSize: 13.5,
    transition: "color 0.2s",
  };

  return (
    <footer
      style={{
        background: "#090909",
        color: "#fff",
        padding: "min(60px,6vw) 24px 25px",
      }}
    >
      <div style={{ maxWidth: 1312, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2.8fr 1fr 1fr 1fr",
            gap: 48,
            marginBottom: 64,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 20,
              }}
            >
              <img src="/assets/images/new/logo.png" width="50" />
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  fontFamily: "Georgia,serif",
                }}
              >
                Fruits Garden
              </span>
            </div>
            <p
              style={{
                fontSize: 13.5,
                color: "rgba(255,255,255,0.45)",
                lineHeight: 1.4,
                maxWidth: 400,
              }}
            >
              UAE's trusted FFV supply company since 2002. Premium quality fresh
              fruits & vegetables for the HORECA sector.
            </p>
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: "rgba(255,255,255,0.35)",
                lineHeight: 1.6,
              }}
            >
              <div>
                <i className="bi bi-geo-alt-fill"></i> Dubai Food District,
                Block 3, Unit 98-99
              </div>
              <div>
                <i className="bi bi-telephone"></i>{" "}
                <a
                  href="tel:+971565378650"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  +971 56 537 8650
                </a>
              </div>
              <div>
                <i className="bi bi-envelope"></i>{" "}
                <a
                  href="mailto:sales@fruitsgarden.com"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  sales@fruitsgarden.com
                </a>
              </div>
              <div>
                <i className="bi bi-globe2"></i>{" "}
                <a
                  href="https://www.fruitsgarden.com"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  www.fruitsgarden.com
                </a>
              </div>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.h}>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 2.5,
                  color: "#dd3c30",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                {col.h}
              </div>
              <ul
                style={{
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {col.ls.map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <Link
                        href={item.path}
                        target="_blank"
                        rel="noreferrer"
                        style={linkStyle}
                        onMouseEnter={(e) => (e.target.style.color = "#fff")}
                        onMouseLeave={(e) =>
                          (e.target.style.color = "rgba(255,255,255,0.45)")
                        }
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <Link
                        to={item.path}
                        style={linkStyle}
                        onMouseEnter={(e) => (e.target.style.color = "#fff")}
                        onMouseLeave={(e) =>
                          (e.target.style.color = "rgba(255,255,255,0.45)")
                        }
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            paddingTop: 20,
            fontSize: 12.5,
            color: "rgba(255,255,255,0.3)",
            textAlign: "center",
          }}
        >
          © 2025 Fruits Garden Trading LLC. All rights reserved. Dubai, UAE.
        </div>
      </div>
    </footer>
  );
}

function BackTop({ scrollY }) {
  if (scrollY < 600) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      style={{
        position: "fixed",
        bottom: 32,
        right: 32,
        zIndex: 999,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: "#dd3c30",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        fontSize: 18,
        boxShadow: "0 4px 18px rgba(39,101,48,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s,box-shadow 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(39,101,48,0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 18px rgba(39,101,48,0.45)";
      }}
    >
      ↑
    </button>
  );
}

function Home() {
  const scrollY = useScrollY();
  useEffect(() => {
    document.title = "Fruits Garden Trading LLC — Quality Comes First";
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return (
    <div>
      <Navbar scrollY={scrollY} />
      <HeroSection scrollY={scrollY} />
      <SecondaryNav scrollY={scrollY} />
      <HighlightsSection />
      <ProductsSection />
      <FreshnessSection />
      {/* <VarietySection /> */}
      <SupplyChainSection />
      <GlobalNetworkSection />
      <CustomersSection />
      {/* <StrengthSection /> */}
      <StatsSection />
      <GallerySection />
      {/* <FruitMosaicSection /> */}
      <StorySection />
      <Footer />
      <BackTop scrollY={scrollY} />
    </div>
  );
}

export default Home;
